import { useState, useEffect } from "react";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";

export default function EditStatusEntryDialog({ open, onOpenChange, caseId, entry, onUpdated }) {
  const [date, setDate] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (entry) {
      setDate((entry.changed_at || "").slice(0, 10));
      setNote(entry.note || "");
    }
  }, [entry]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch(`/cases/${caseId}/status/${entry.id}`, { date, note });
      toast({ title: "Timeline entry updated" });
      onUpdated();
      onOpenChange(false);
    } catch (err) {
      toast({ variant: "destructive", title: "Could not update entry", description: formatApiErrorDetail(err.response?.data?.detail) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="edit-status-entry-dialog" className="rounded-2xl">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Edit Timeline Entry</DialogTitle>
        </DialogHeader>
        {entry && (
          <form onSubmit={handleSave} className="space-y-4">
            <div>
              <Label htmlFor="edit-entry-date">Date</Label>
              <Input id="edit-entry-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="edit-entry-date-input" className="mt-1.5 h-11" />
            </div>
            <div>
              <Label htmlFor="edit-entry-note">Note</Label>
              <Textarea id="edit-entry-note" value={note} onChange={(e) => setNote(e.target.value)} data-testid="edit-entry-note-input" className="mt-1.5 min-h-[80px]" />
            </div>
            <DialogFooter>
              <Button type="submit" disabled={saving} className="w-full h-11 bg-amtm-teal text-white hover:bg-amtm-tealHover" data-testid="save-edit-entry-button">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Changes"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
